import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Icon
} from '@mui/material';
import axios from 'axios';
import { useQuery } from 'react-query';

import { Loading } from '../components/Loading';
import { ErrorMessage, Field } from 'formik';
import debounce from 'lodash.debounce';

export default function CampoTelefonoConCodigo({
  selectedCountry: propSelectedCountry,
  setSelectedCountry: propSetSelectedCountry,
  nombre,
  etiqueta,
  max = null,
  requerido = false,
  touched,
  errors,
  deshabilitado = null,
  setFieldValue,
  codigoPais = null,
  tamano = 'large',
  ...props
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [internalSelectedCountry, setInternalSelectedCountry] = useState(null);
  const searchInputRef = useRef(null);

  const isControlled = propSelectedCountry !== undefined && typeof propSetSelectedCountry === 'function';
  const selectedCountry = isControlled ? propSelectedCountry : (propSelectedCountry || internalSelectedCountry);
  const setSelectedCountry = useCallback((country) => {
    if (typeof propSetSelectedCountry === 'function') {
      propSetSelectedCountry(country);
    }
    setInternalSelectedCountry(country);
  }, [propSetSelectedCountry]);

  const getPaises = async () => {
    const apiUrl = process.env.REACT_APP_URL || 'https://apitest.rauhtech.com/v2/misrifas';
    const requestUrl = `${apiUrl}/paises/listar`;
    try {
      const res = await axios.get(requestUrl);
      const data = res.data?.data !== undefined ? res.data.data : res.data;
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('[API-V2] GET /paises/listar - Error:', err?.response?.data || err?.message || err);
      throw err;
    }
  };

  const { data: countries = [], isLoading, isError, error } = useQuery('paises', getPaises, {
    staleTime: Infinity,
  });

  useEffect(() => {
    if (codigoPais && Array.isArray(countries) && countries.length > 0) {
      const country = countries.find(
        (c) => String(c.prefijo).replace(/\+/g, '') === String(codigoPais).replace(/\+/g, '')
      );
      if (country) {
        setSelectedCountry(country);
        if (setFieldValue) setFieldValue('codigo_pais', country.prefijo);
      }
    }
  }, [codigoPais, countries, setSelectedCountry, setFieldValue]);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const debouncedSetSearch = useCallback(
    debounce((value) => {
      setSearch(value);
    }, 200),
    []
  );

  const handleSearchChange = (e) => {
    debouncedSetSearch(e.target.value);
  };

  const filteredCountries = useMemo(() => {
    if (!Array.isArray(countries)) return [];
    const lowerSearch = (search || '').trim().toLowerCase();
    if (!lowerSearch) return countries;
    return countries.filter(
      (country) =>
        (country?.nombre && country.nombre.toLowerCase().includes(lowerSearch)) ||
        (country?.prefijo && String(country.prefijo).includes(lowerSearch)) ||
        (country?.codigo && country.codigo.toLowerCase().includes(lowerSearch))
    );
  }, [countries, search]);

  const handleSelectCountry = (country) => {
    setSelectedCountry(country);
    if (setFieldValue) {
      setFieldValue('codigo_pais', country.prefijo);
    }
    setOpen(false);
  };

  const handleClearCountry = (e) => {
    e.stopPropagation();
    setSelectedCountry(null);
    if (setFieldValue) {
      setFieldValue('codigo_pais', '');
    }
  };

  const handleOpenModal = () => {
    if (deshabilitado) return;
    setSearch('');
    setOpen(true);
  };

  const prefixDisplay = selectedCountry
    ? `${String(selectedCountry.prefijo).startsWith('+') ? '' : '+'}${selectedCountry.prefijo}`
    : '';

  return (
    <Box>
      <Grid container spacing={1}>
        <Grid item xs={4} sm={4}>
          <Box onClick={handleOpenModal} sx={{ cursor: deshabilitado ? 'default' : 'pointer' }}>
            <Field
              size={tamano}
              as={TextField}
              fullWidth
              required={requerido}
              id="codigo_pais"
              name="codigo_pais"
              label="Código"
              autoComplete="off"
              disabled={deshabilitado}
              value={prefixDisplay}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                style: { cursor: deshabilitado ? 'default' : 'pointer', fontWeight: 700, color: theme.palette.text.primary },
                readOnly: true,
              }}
              InputProps={{
                startAdornment: selectedCountry?.codigo ? (
                  <InputAdornment position="start" sx={{ mr: 0.5 }}>
                    <Avatar
                      src={`https://flagcdn.com/w40/${selectedCountry.codigo.toLowerCase()}.png`}
                      alt={selectedCountry.nombre || 'Bandera'}
                      sx={{ width: 22, height: 16, borderRadius: 0.5 }}
                    />
                  </InputAdornment>
                ) : null,
                endAdornment: selectedCountry && !deshabilitado ? (
                  <InputAdornment position="end">
                    <IconButton onClick={handleClearCountry} size="small" edge="end">
                      <Icon sx={{ fontSize: 18, color: theme.palette.text.secondary }}>
                        clear
                      </Icon>
                    </IconButton>
                  </InputAdornment>
                ) : null,
                readOnly: true,
              }}
              error={Boolean(touched && touched['codigo_pais'] && errors && errors['codigo_pais'])}
              helperText={
                touched &&
                touched['codigo_pais'] &&
                errors &&
                errors['codigo_pais'] && <ErrorMessage name="codigo_pais" />
              }
            />
          </Box>
        </Grid>

        <Grid item xs={8} sm={8}>
          <Field
            size={tamano}
            as={TextField}
            fullWidth
            required={requerido}
            id={nombre}
            name={nombre}
            type="tel"
            label={etiqueta}
            autoComplete="off"
            {...props}
            disabled={deshabilitado}
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 20 }}>
                    phone
                  </Icon>
                </InputAdornment>
              ),
              inputProps: { maxLength: max, inputMode: 'numeric', style: { color: theme.palette.text.primary, fontWeight: 600 } },
              onKeyPress: (event) => {
                if (!/[0-9]/.test(event.key)) {
                  event.preventDefault();
                }
              },
            }}
            error={Boolean(touched && touched[nombre] && errors && errors[nombre])}
            helperText={
              touched &&
              touched[nombre] &&
              errors &&
              errors[nombre] && <ErrorMessage name={nombre} />
            }
          />
        </Grid>
      </Grid>

      <Dialog
        fullScreen={isMobile}
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            overflow: 'hidden',
            bgcolor: theme.palette.mode === 'dark' ? '#131b2e' : '#ffffff',
            color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
            boxShadow: theme.palette.mode === 'dark' ? '0 25px 50px rgba(0, 0, 0, 0.6)' : '0 20px 50px rgba(0, 0, 0, 0.15)',
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: theme.palette.mode === 'dark' ? '#1e293b' : '#2d3484',
            color: 'white',
            py: 2,
            px: 2.5,
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.06)',
              top: -20,
              right: -10,
              pointerEvents: 'none',
            }}
          />
          <Box display="flex" justifyContent="space-between" alignItems="center" position="relative" zIndex={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Icon sx={{ color: 'white' }}>public</Icon>
              <Typography variant="h6" sx={{ color: '#ffffff', fontWeight: 800, fontSize: '1.15rem' }}>
                Seleccionar País
              </Typography>
            </Box>
            <IconButton onClick={() => setOpen(false)} size="small" sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '6px' }}>
              <Icon sx={{ fontSize: 20 }}>close</Icon>
            </IconButton>
          </Box>
        </DialogTitle>

        {/* Buscador dentro del cuerpo */}
        <Box sx={{ p: 2, bgcolor: theme.palette.background.paper, borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0' }}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Buscar por país o prefijo..."
            onChange={handleSearchChange}
            inputRef={searchInputRef}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Icon sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 20 }}>search</Icon>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#f8fafc',
                '& fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0' },
                '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main },
              },
            }}
          />
        </Box>

        <DialogContent sx={{ p: 0, bgcolor: theme.palette.background.paper }}>
          {isLoading ? (
            <Box p={4} display="flex" justifyContent="center">
              <Loading />
            </Box>
          ) : isError ? (
            <Box p={3} textAlign="center">
              <Typography color="error" variant="body2" fontWeight={600}>
                Error cargando países: {error?.message || 'Error desconocido'}
              </Typography>
            </Box>
          ) : (
            <List sx={{ pt: 0, pb: 0 }}>
              {filteredCountries?.map((country) => {
                const isSelected = selectedCountry?.codigo === country.codigo;
                return (
                  <ListItem key={country.codigo || country.id || country.prefijo} disablePadding divider>
                    <ListItemButton
                      onClick={() => handleSelectCountry(country)}
                      sx={{
                        py: 1.2,
                        px: 2.5,
                        bgcolor: isSelected ? (theme.palette.mode === 'dark' ? 'rgba(129, 140, 248, 0.15)' : 'rgba(45, 52, 132, 0.08)') : 'transparent',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(45, 52, 132, 0.04)',
                        },
                      }}
                    >
                      <ListItemAvatar sx={{ minWidth: 40 }}>
                        <Avatar
                          src={`https://flagcdn.com/w40/${country.codigo?.toLowerCase()}.png`}
                          alt={country.nombre}
                          sx={{ width: 28, height: 20, borderRadius: '4px' }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={country.nombre}
                        secondary={country.nombre_oficial && country.nombre_oficial !== country.nombre ? country.nombre_oficial : null}
                        primaryTypographyProps={{
                          fontWeight: isSelected ? 800 : 600,
                          color: isSelected ? (theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main) : theme.palette.text.primary,
                          fontSize: '0.92rem',
                        }}
                        secondaryTypographyProps={{
                          fontSize: '0.78rem',
                          color: theme.palette.text.secondary,
                        }}
                      />
                      <Box
                        sx={{
                          bgcolor: isSelected ? (theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main) : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#f1f5f9'),
                          color: isSelected ? '#ffffff' : theme.palette.text.secondary,
                          px: 1.2,
                          py: 0.4,
                          borderRadius: '6px',
                          fontWeight: 800,
                          fontSize: '0.82rem',
                          display: 'inline-flex',
                          alignItems: 'center',
                        }}
                      >
                        +{country.prefijo}
                      </Box>
                    </ListItemButton>
                  </ListItem>
                );
              })}
              {filteredCountries?.length === 0 && (
                <Box p={4} textAlign="center">
                  <Icon sx={{ fontSize: 32, color: theme.palette.text.secondary, mb: 1 }}>search_off</Icon>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontWeight: 600 }}>
                    No se encontraron países.
                  </Typography>
                </Box>
              )}
            </List>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
